import { config } from '../config/config';
// --------------------
// MAPBOX TYPINGS
// --------------------

// Generated by https://quicktype.io
export interface MapboxResult {
  type: string;
  features: Feature[];
  attribution: string;
}

export interface Feature {
  type: string;
  id: string;
  geometry: Geometry;
  properties: Properties;
}

export interface Geometry {
  type: string;
  coordinates: number[];
}

export interface Properties {
  mapbox_id: string;
  feature_type: string;
  full_address: string;
  name: string;
  coordinates: Coordinates;
  place_formatted: string;
  context: Context;
}

export interface Context {
  address: Address;
  street: Neighborhood;
  neighborhood: Neighborhood;
  postcode: Neighborhood;
  place: District;
  district: District;
  region: Region;
  country: Country;
}

export interface Address {
  mapbox_id: string;
  address_number: string;
  street_name: string;
  name: string;
}

export interface Country {
  mapbox_id: string;
  name: string;
  wikidata_id: string;
  country_code: string;
  country_code_alpha_3: string;
}

export interface District {
  mapbox_id: string;
  name: string;
  wikidata_id: string;
}

export interface Neighborhood {
  mapbox_id: string;
  name: string;
}

export interface Region {
  mapbox_id: string;
  name: string;
  wikidata_id: string;
  region_code: string;
  region_code_full: string;
}

export interface Coordinates {
  longitude: number;
  latitude: number;
  accuracy: string;
  routable_points: RoutablePoint[];
}

export interface RoutablePoint {
  name: string;
  latitude: number;
  longitude: number;
}

export interface MapBoxGeocodeResponse extends Response {
  json(): Promise<MapboxResult>;
}

export interface ForwardGecodingOptions {
  type?:
    | 'address'
    | 'district'
    | 'neighborhood'
    | 'place'
    | 'region'
    | 'country'
    | 'locality';
  limit?: number;
  country?: string;
  proximity?: 'ip' | [number, number] | 'none';
}

// --------------------
// MAPBOX HELPERS
// --------------------

// This file is for helpers related to mapbox fetching
const mapboxUrl = new URL('https://api.mapbox.com');
const endpoints = {
  reversev6: '/search/geocode/v6/reverse',
  forwardv6: '/search/geocode/v6/forward',
};
const API_KEY = config.mapbox.apiKey;

// Example query
// ?limit=1&longitude=-73.96137258024162&latitude=40.78368564869686&access_token=pk.eyJ1IjoicGxhdGZvcm10ZWFtYXJib2wiLCJhIjoiY2w1YTAycWppMmlpMzNrbXIyYnFzZTh0cyJ9.PqkHoimkxW7ywXEV9j0Eqg

function getReverseGeocodeUrl(longitude: number, latitude: number) {
  return `${endpoints.reversev6}?limit=1&longitude=${longitude}&latitude=${latitude}`;
}

const defaults: ForwardGecodingOptions = {
  type: 'address',
  limit: 1,
  proximity: 'none',
};

function forwardGeocodeUrl(
  query: string,
  options: ForwardGecodingOptions = {
    type: 'address',
    limit: 1,
    proximity: 'none',
  },
) {
  const optionsWithDefaults = { ...defaults, ...options };
  const encodedQuery = encodeURIComponent(query);
  let args = `?q=${encodedQuery}&type=${optionsWithDefaults.type}&limit=${optionsWithDefaults.limit}`;
  const proximity = Array.isArray(optionsWithDefaults.proximity)
    ? optionsWithDefaults.proximity.join(',')
    : optionsWithDefaults.proximity;
  if (proximity && proximity !== 'none') {
    args += `&proximity=${proximity}`;
  }

  return `${endpoints.forwardv6}${args}`;
}

// adds the path, query and appends the base url and also adds the api key
function mapboxFetch(path: string) {
  const accessToken = `${
    path.includes('?') ? '&' : '?'
  }access_token=${API_KEY}`;

  const fetchUrl = `${mapboxUrl.origin}${path}${accessToken}`;
  return fetch(fetchUrl) as Promise<MapBoxGeocodeResponse>;
}

export function fetchReverseGeocode(longitude: number, latitude: number) {
  const url = getReverseGeocodeUrl(longitude, latitude);
  return mapboxFetch(url);
}

export function fetchForwardGeocode(query: string) {
  const url = forwardGeocodeUrl(query);
  return mapboxFetch(url);
}

export function fetchCountyLocation(county: string, state: string) {
  // {county} County, State has better results
  const suffix = county.includes(' County') ? '' : ' County';

  const url = forwardGeocodeUrl(`${county}${suffix}, ${state}`, {
    proximity: 'none',
    type: 'district',
  });

  return mapboxFetch(url).then((res) => res.json());
}
